import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeading,
  IconButton,
  Typography,
} from 'omakase';
import { Card, CardContent } from 'ui';
import { IconAdd, IconSubtract } from 'ui/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { IconMinus, IconPlusLarge } from '@tock/icons';
import { core } from '@tock/coretypes';
import { cropImageUrl } from '@tock/images';
import { generateModifierDialogProps, isOptionGroupValid } from '@tock/shared/utils/option';

import { Theme, css } from '@emotion/react';
import { useAddonsPriceDisplayDetails } from 'utils/priceDisplayUtils';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import ConsumerDisplayMoney from 'components/ConsumerDisplayMoney';
import LazyImage from '@tock/shared/components/LazyImage';
import ModifierGroupCompact from 'components/business/addons/ModifierGroupCompact';
import React, { useEffect } from 'react';
import _ from 'lodash';

const cardRootStyles = (theme: Theme) => css`
  margin-bottom: ${theme.spacing(1.5)}px;
`;

const cardContentStyles = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  padding: ${theme.spacing(2)}px ${theme.spacing(2.5)}px;
  white-space: pre-wrap;
  ${theme.breakpoints.up('sm')} {
    padding: ${theme.spacing(2)}px ${theme.spacing(3.5)}px;
  }
`;

const confirmButtonStyles = (theme: Theme) => css`
  display: flex;
  justify-content: space-between;
  position: relative;
  width: 50%;

  ${theme.breakpoints.up('sm')} {
    justify-content: center;
  }
`;

const dialogContentStyles = (theme: Theme) => css`
  background-color: ${theme.colors.bgLight};
  padding-top: 0;
  padding-bottom: 0;
`;

const quantityStyles = (theme: Theme) => css`
  align-items: center;
  display: flex;
  justify-content: center;
  width: calc(50% - ${theme.spacing(2)}px);

  ${theme.breakpoints.up('sm')} {
    width: calc(50% - ${theme.spacing(3)}px);
  }
`;

const quantityTextStyles = (theme: Theme) => css`
  margin-left: ${theme.spacing(2.75)}px;
  width: ${theme.spacing(8)}px;
  text-align: left;
`;

const actionsStyles = css`
  flex-direction: row;
  justify-content: space-between;
`;

const imageStyles = (theme: Theme) => css`
  width: 100%;
  display: block;
  height: auto;
  ${theme.breakpoints.up('sm')} {
    width: 100%;
  }
`;

interface ModalProps {
  isOpen: boolean;
  isEditing: boolean;
  onClose(): void;
  onComplete(optionOrders: core.IOptionOrder[]): void;
}

interface Props {
  ticketType: core.ITicketType;
  ticketOption: core.ITicketOption;
  ticketOptionGroup: core.ITicketOptionGroup;
  modifierOptionGroups: core.ITicketOptionGroup[];
  allTicketOptions: core.ITicketOption[];
  optionOrders: core.IOptionOrder[];
  otherOptionOrders: core.IOptionOrder[];
  ticketOptionPrices: core.IPrice[];
  partySize: number;
  onChangeQuantity(selectedOptions: core.IOptionOrder[]): void;
  patron?: core.IPatron;
}

const ModifierDialog: React.FunctionComponent<Props & ModalProps> = ({
  ticketType,
  ticketOption,
  ticketOptionGroup,
  modifierOptionGroups,
  allTicketOptions,
  optionOrders,
  otherOptionOrders,
  ticketOptionPrices,
  partySize,
  isOpen,
  onClose,
  onComplete,
  onChangeQuantity,
  isEditing,
  patron,
}) => {
  const isTouch = !document.body.classList.contains('no-touch');
  const theme = useTheme();
  const matchesSm = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    /**
     * hack to set position fixed on mobile as a means to get iOS to always
     * show the menu bar. This prevents iOS from intercepting clicks
     * on the bottom of the page.
     */
    if (isTouch && matchesSm) {
      const pageYOffset = window.pageYOffset;
      document.body.style.position = 'fixed';

      return () => {
        document.body.style.position = '';
        window.scrollTo(0, pageYOffset);
      };
    }

    return;
  }, [isOpen, isTouch, matchesSm]);

  const firstOrder = _.first(optionOrders);
  const modifierOrders = firstOrder?.modifierOption || [];
  const handleAddItem = () => {
    onComplete(optionOrders);
  };
  const handleChangeModifiers = (modifiers: core.IOptionOrder[]) => {
    _.forEach(optionOrders, (optionOrder) => {
      optionOrder.modifierOption = modifiers;
    });
    onChangeQuantity(optionOrders);
  };
  const isConfirmButtonEnabled = () => {
    return _.every(modifierOptionGroups, (ticketOptionGroup) => {
      const options = _.filter(allTicketOptions, (option) => {
        return option.ticketOptionGroupId === ticketOptionGroup.id;
      });
      return isOptionGroupValid(firstOrder?.modifierOption, options, partySize, ticketOptionGroup);
    });
  };
  const optionGroups = _.map(modifierOptionGroups, (ticketOptionGroup) => {
    const ticketOptions = _.filter(allTicketOptions, {
      ticketOptionGroupId: ticketOptionGroup.id,
      backingMode: ticketOptionGroup.backingMode,
    });

    const pricesOfOptionsInGroup = _.map(ticketOptions, (ticketOption) => {
      const relevantTemplatedOption = _.find(ticketOptionPrices, {
        id: ticketOption.id,
        backingMode: ticketOption.backingMode,
      });
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return relevantTemplatedOption!;
    });

    return (
      <Card key={ticketOptionGroup.id ?? ''} css={cardRootStyles} elevation={0}>
        <CardContent css={cardContentStyles}>
          <ModifierGroupCompact
            isOpen={true}
            isCompleted={false}
            isSkipped={false}
            key={ticketOptionGroup.id as number}
            partySize={1}
            optionOrders={modifierOrders}
            ticketOptions={ticketOptions}
            ticketOptionGroup={ticketOptionGroup}
            ticketOptionPrices={pricesOfOptionsInGroup}
            ticketType={ticketType}
            onChangeQuantity={handleChangeModifiers}
            patron={patron}
          />
        </CardContent>
      </Card>
    );
  });

  const { useInclusivePricing, feeDisclaimerLabel, showFeeDisclaimerLabel } =
    useAddonsPriceDisplayDetails(ticketType);

  const {
    quantity,
    disableDecrement,
    disableIncrement,
    handleDecrementQuantity,
    handleIncrementQuantity,
    price,
  } = generateModifierDialogProps({
    ticketType,
    ticketOption,
    ticketOptionGroup,
    allTicketOptions,
    optionOrders,
    otherOptionOrders,
    ticketOptionPrices,
    partySize,
    onChangeQuantity,
    patron,
    useInclusivePricing,
  });
  const { image } = ticketOption;

  return (
    <Dialog
      variant="partial"
      aria-labelledby="modifier-dialog"
      open={isOpen}
      onClose={onClose}
      data-testid="modifier-dialog"
      fullWidth
    >
      <DialogHeading id="modifier-dialog" onClose={onClose}>
        {ticketOption.name}
      </DialogHeading>
      <DialogContent disablePadding css={dialogContentStyles}>
        {!!image && (
          <LazyImage
            alt={image.altText ?? undefined}
            preload={true}
            css={imageStyles}
            src={cropImageUrl(image.imageUrl ?? '', 1440, 720)}
            backgroundColor={image.dominantColor ?? ''}
          />
        )}
        {(ticketOption.description || useInclusivePricing) && (
          <Card css={cardRootStyles} elevation={0}>
            {ticketOption.description && (
              <CardContent css={cardContentStyles}>
                <Typography variant="body02" component="p">
                  {ticketOption.description}
                </Typography>
              </CardContent>
            )}
            {showFeeDisclaimerLabel && (
              <CardContent css={cardContentStyles}>
                <Typography variant="caption" color="fgSubtle">
                  {feeDisclaimerLabel}
                </Typography>
              </CardContent>
            )}
          </Card>
        )}
        {optionGroups}
        <Card css={cardRootStyles} elevation={0}></Card>
      </DialogContent>
      <DialogActions css={actionsStyles}>
        {/* TODO: replace with quantity picker */}
        <div css={quantityStyles}>
          <IconButton
            size="medium"
            variant="ghost"
            aria-label={`Decrease ${name} quantity`}
            data-testid={`supplement-minus-one-supplement-id-${ticketOption.id}`}
            disabled={disableDecrement}
            onClick={handleDecrementQuantity}
          >
            <IconMigrationUtil
              legacyIcon={<IconSubtract data-testid="decrease-button" />}
              newIcon={<IconMinus data-testid="decrease-button" />}
            />
          </IconButton>
          <Typography
            variant="body02Strong"
            component="p"
            css={quantityTextStyles}
            data-testid="quantity-text"
            selected-value={quantity}
          >
            Qty: {quantity}
          </Typography>
          <IconButton
            size="medium"
            variant="ghost"
            aria-label={`Increase ${name} quantity`}
            data-testid={`supplement-plus-one-supplement-id-${ticketOption.id}`}
            disabled={disableIncrement}
            onClick={handleIncrementQuantity}
          >
            <IconMigrationUtil
              legacyIcon={<IconAdd data-testid="increase-button" />}
              newIcon={<IconPlusLarge data-testid="increase-button" />}
            />
          </IconButton>
        </div>
        <Button
          css={confirmButtonStyles}
          disabled={!isConfirmButtonEnabled()}
          variant="primary"
          size="large"
          onClick={handleAddItem}
          data-testid="add-item-button"
        >
          {/* The inner button span is included to prevent a visual bug where Safari 14.1.1 is not
          correctly repainting the button text upon change from disabled to enabled. Ideally, we would not add an extra span inside the <Button> */}
          <span>
            {isEditing ? 'Update' : 'Add'}
            {price > 0 && (
              <span>
                &nbsp; &middot; &nbsp;
                <ConsumerDisplayMoney amount={price} />
              </span>
            )}
          </span>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModifierDialog;
